import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Send, Loader2, Plus, Brain, Trash2, Check, MessageSquare } from "lucide-react";

const SUGGESTIONS = [
  "Chi devo richiamare oggi?",
  "Clienti fermi da 4 mesi",
  "Cosa ho in agenda oggi?",
  "Dammi 3 mosse per questa settimana",
];

const newSid = () => `cw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function ActionCard({ a, onDone }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const label = a.type === "create_reminder"
    ? `${a.appointment ? "Appuntamento" : "Promemoria"}: ${a.title || ""} — ${(a.when || "").replace("T", " ")}${a.client_name ? ` (${a.client_name})` : ""}`
    : a.type === "set_stato"
      ? `Stato → ${a.stato}${a.client_name ? ` per ${a.client_name}` : ""}`
      : a.type === "set_recall"
        ? `Recall → ${a.data}${a.client_name ? ` per ${a.client_name}` : ""}`
        : a.type;
  const run = async () => {
    setBusy(true);
    try {
      if (a.type === "create_reminder") {
        await api.post("/reminders", {
          clientId: a.client_id || null, title: a.title || "Promemoria", when: a.when,
          appointment: !!a.appointment, apptType: a.apptType || null, notes: "Creato dal Copilota AI",
        });
      } else if (a.type === "set_stato") {
        await api.patch(`/clients/${a.client_id}`, { stato: a.stato });
      } else if (a.type === "set_recall") {
        await api.patch(`/clients/${a.client_id}`, { data: a.data });
      } else { throw new Error("azione sconosciuta"); }
      setDone(true);
      toast.success("Fatto ✓");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Azione fallita");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="copilot-action">
      <span className="flex-1 text-[11.5px] text-slate-700 leading-snug">{label}</span>
      {done ? (
        <span className="text-emerald-600 text-[11px] font-bold flex items-center gap-1"><Check className="w-3.5 h-3.5" /> fatto</span>
      ) : (
        <button onClick={run} disabled={busy} data-testid="copilot-action-confirm"
          className="px-2.5 py-1.5 text-[11px] font-bold text-white bg-[#0A192F] rounded-md hover:bg-[#13294B] disabled:opacity-50 shrink-0">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Conferma"}
        </button>
      )}
    </div>
  );
}

export default function CopilotWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("chat");
  const [sid, setSid] = useState(() => sessionStorage.getItem("copilot-sid") || newSid());
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [memory, setMemory] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => { sessionStorage.setItem("copilot-sid", sid); }, [sid]);
  useEffect(() => {
    if (!open) return;
    api.get(`/copilot/session/${sid}`).then((r) => setMsgs(r.data?.messages || [])).catch(() => {});
  }, [open, sid]);
  useEffect(() => {
    if (open && tab === "memoria") api.get("/copilot/memory").then((r) => setMemory(r.data?.items || [])).catch(() => {});
  }, [open, tab]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, busy, open]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: t }]);
    setBusy(true);
    try {
      const r = await api.post("/copilot/chat", { session_id: sid, message: t });
      setMsgs((m) => [...m, { role: "assistant", text: r.data.reply, actions: r.data.actions || [] }]);
      if ((r.data.learned || []).length) toast.info(`🧠 Ho imparato: ${r.data.learned[0]}`);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: e?.response?.data?.detail || "Errore: riprova tra poco.", actions: [] }]);
    } finally { setBusy(false); }
  };

  const nuovaChat = () => { const s = newSid(); setSid(s); setMsgs([]); };
  const { pathname } = useLocation();
  const hideFab = pathname.startsWith("/intelligence/articolo") && !open;
  const delMemory = async (id) => {
    await api.delete(`/copilot/memory/${id}`).catch(() => {});
    setMemory((m) => m.filter((i) => i.id !== id));
  };

  return (
    <>
      {!hideFab && <button onClick={() => setOpen((v) => !v)} data-testid="copilot-fab"
        title="Copilota AI"
        className="fixed z-40 w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white hover:scale-105 active:scale-95 transition"
        style={{ right: 20, bottom: "calc(168px + env(safe-area-inset-bottom, 0px))", background: "linear-gradient(135deg, #0A192F 0%, #274472 100%)", border: "2px solid #B7955B" }}>
        <Sparkles className="w-6 h-6" style={{ color: "#D4AB6E" }} />
      </button>}

      {open && (
        <div className="fixed z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          style={{ right: 16, bottom: 96, width: "min(400px, calc(100vw - 32px))", height: "min(600px, calc(100vh - 140px))" }}
          data-testid="copilot-panel">
          {/* Header */}
          <div className="px-4 py-3 flex items-center gap-2 shrink-0" style={{ background: "#0A192F" }}>
            <Sparkles className="w-5 h-5" style={{ color: "#D4AB6E" }} />
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold text-sm leading-tight">Copilota AI</div>
              <div className="text-[10px] text-white/50">GPT collegato ai tuoi dati · impara da te</div>
            </div>
            <button onClick={() => setTab(tab === "memoria" ? "chat" : "memoria")} data-testid="copilot-memory-tab"
              title="Cosa ha imparato di te"
              className={`p-1.5 rounded-lg ${tab === "memoria" ? "bg-[#B7955B] text-white" : "text-white/60 hover:text-white"}`}>
              <Brain className="w-4 h-4" />
            </button>
            <button onClick={nuovaChat} title="Nuova chat" data-testid="copilot-new-chat"
              className="p-1.5 rounded-lg text-white/60 hover:text-white"><Plus className="w-4 h-4" /></button>
            <button onClick={() => setOpen(false)} data-testid="copilot-close"
              className="p-1.5 rounded-lg text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          {tab === "memoria" ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="copilot-memory-list">
              <div className="text-xs text-slate-500 mb-2">Cose che il Copilota ha imparato su di te (le usa in ogni risposta). Digli "ricordati che..." per insegnargli qualcosa.</div>
              {memory.length === 0 && <div className="text-sm text-slate-400 text-center py-8">Ancora nessun apprendimento.<br />Scrivigli come lavori e lo ricorderà.</div>}
              {memory.map((i) => (
                <div key={i.id} className="flex items-start gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                  <Brain className="w-3.5 h-3.5 text-[#B7955B] mt-0.5 shrink-0" />
                  <span className="flex-1 text-[12px] text-slate-700">{i.text}</span>
                  <button onClick={() => delMemory(i.id)} data-testid={`copilot-mem-del-${i.id}`}
                    className="p-1 text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50" data-testid="copilot-messages">
                {msgs.length === 0 && !busy && (
                  <div className="pt-6 text-center space-y-3">
                    <MessageSquare className="w-8 h-8 text-slate-300 mx-auto" />
                    <div className="text-sm text-slate-500 px-4">Chiedimi qualsiasi cosa sui tuoi clienti, l'agenda o cosa fare oggi.</div>
                    <div className="flex flex-wrap gap-1.5 justify-center px-2">
                      {SUGGESTIONS.map((s) => (
                        <button key={s} onClick={() => send(s)} data-testid="copilot-suggestion"
                          className="px-2.5 py-1.5 text-[11px] bg-white border border-slate-200 rounded-full text-slate-600 hover:border-[#B7955B] hover:text-[#8a6e3d]">
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {msgs.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#0A192F] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}
                      data-testid={m.role === "user" ? "copilot-msg-user" : "copilot-msg-ai"}>
                      {m.text}
                      {(m.actions || []).length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {m.actions.map((a, j) => <ActionCard key={j} a={a} />)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2.5 flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#B7955B]" />
                      <span className="text-[11px] text-slate-400">sto guardando i tuoi dati…</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-2.5 border-t border-slate-200 bg-white flex items-center gap-2 shrink-0">
                <input value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                  placeholder="Scrivi al Copilota…" data-testid="copilot-input"
                  className="flex-1 px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#B7955B]" />
                <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="copilot-send"
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white disabled:opacity-40 shrink-0"
                  style={{ background: "#0A192F" }}>
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
